import { getProductList } from '@/services/ProductsService/product'

/**
 * Extracts product IDs from selectedProducts array
 * @param {Array} selectedProducts - Array of selected product objects
 * @returns {Array} Array of product IDs
 */
export const extractProductIds = (selectedProducts) => {
  if (!selectedProducts || selectedProducts.length === 0) {
    return []
  }

  return selectedProducts
    .map(product => product.value)
    .filter(Boolean) // Remove any falsy values
}

/**
 * Extracts and sorts product IDs by priority from existing fetched products
 * @param {Array} selectedProducts - Array of selected product objects
 * @param {Array} existingFetchedProducts - Array of existing fetched products with priority
 * @returns {Array} Array of product IDs sorted by priority
 */
export const extractProductIdsByPriority = (selectedProducts, existingFetchedProducts = []) => {
  if (!selectedProducts || selectedProducts.length === 0) {
    return []
  }

  // Create a map of existing products by productId for quick priority lookup
  const existingProductsMap = new Map()
  existingFetchedProducts.forEach(product => {
    if (product?.productId && product?.priority) {
      existingProductsMap.set(product.productId, product.priority)
    }
  })

  // Extract product IDs and sort by priority
  const productIdsWithPriority = selectedProducts
    .map(product => ({
      productId: product.value,
      priority: existingProductsMap.get(product.value) || 999 // Default priority for new products
    }))
    .filter(item => Boolean(item.productId)) // Remove any falsy product IDs
    .sort((a, b) => a.priority - b.priority) // Sort by priority (ascending)
    .map(item => item.productId) // Extract just the product IDs

  return productIdsWithPriority
}

/**
 * Creates search array in the required format for API request
 * @param {Array} productIds - Array of product IDs
 * @returns {Array} Search array for API request
 */
export const createSearchArray = (productIds) => {
  if (productIds.length === 0) {
    return []
  }

  return [{
    field: 'productId',
    value: productIds.join('|'), // Join IDs with pipe separator
    type: 'string'
  }]
}

/**
 * Creates query parameters for pagination
 * @param {number} page - Page number (default: 0)
 * @param {number} pageSize - Page size (default: 1000)
 * @returns {string} Query parameters string
 */
export const createQueryParams = (page = 0, pageSize = 1000) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  })
  return queryParams.toString()
}

/**
 * Enhances products with priority and details fields
 * @param {Array} products - Array of product objects
 * @returns {Array} Enhanced products with priority and details
 */
export const enhanceProductsWithDetails = (products) => {
  if (!Array.isArray(products)) {
    return []
  }

  return products.map((product, index) => ({
    ...product,
    priority: index + 1, // Priority starts from 1
    details: {
      points: '', // Default points value (empty)
      quota: {
        unlimited: false, // Default to not unlimited
        value: '' // Default quota value (empty)
      },
      inventory: '' // Default inventory value (empty)
    }
  }))
}

/**
 * Reorders products to match the order of product IDs sent in API request
 * @param {Array} products - Array of products returned from API
 * @param {Array} orderedProductIds - Array of product IDs in the order they were sent to API
 * @returns {Array} Products reordered to match the original product ID order
 */
export const reorderProductsByRequestOrder = (products, orderedProductIds) => {
  if (!products || !Array.isArray(products) || !orderedProductIds || !Array.isArray(orderedProductIds)) {
    return products || []
  }

  // Create a map of products by productId for quick lookup
  const productsMap = new Map()
  products.forEach(product => {
    if (product?.productId) {
      productsMap.set(product.productId, product)
    }
  })

  // Reorder products based on the original product ID order
  const reorderedProducts = []
  orderedProductIds.forEach(productId => {
    const product = productsMap.get(productId)
    if (product) {
      reorderedProducts.push(product)
    }
  })

  // Add any products that weren't in the original request (edge case)
  products.forEach(product => {
    if (product?.productId && !orderedProductIds.includes(product.productId)) {
      reorderedProducts.push(product)
    }
  })

  return reorderedProducts
}

/**
 * Preserves existing product details when merging with new product data
 * @param {Array} existingProducts - Array of existing products with details
 * @param {Array} newProducts - Array of new products from API
 * @returns {Array} Merged products with preserved details
 */
export const preserveProductDetails = (existingProducts, newProducts) => {
  if (!existingProducts || !Array.isArray(existingProducts)) {
    return newProducts || []
  }
  
  if (!newProducts || !Array.isArray(newProducts)) {
    return existingProducts
  }

  // Create a map of existing products by productId for quick lookup
  const existingDetailsMap = new Map()
  existingProducts.forEach(product => {
    if (product?.productId && product?.details) {
      existingDetailsMap.set(product.productId, product.details)
    }
  })

  // Merge new products with existing details
  return newProducts.map(product => {
    const existingDetails = existingDetailsMap.get(product.productId)
    
    if (existingDetails) {
      // Preserve existing details
      return {
        ...product,
        details: existingDetails
      }
    } else {
      // Use default details for new products
      return {
        ...product,
        details: {
          points: '',
          quota: {
            unlimited: false,
            value: ''
          },
          inventory: ''
        }
      }
    }
  })
}

/**
 * Fetches products by IDs and enhances them with additional fields
 * @param {Array} selectedProducts - Array of selected product objects
 * @param {Function} updateFetchedProducts - Function to update fetched products in Redux store
 * @param {Function} setProductsLoading - State setter for loading state
 * @param {Function} setProductsError - State setter for error state
 * @param {Array} existingFetchedProducts - Array of existing fetched products to preserve details from
 */
export const fetchAndEnhanceProducts = async (
  selectedProducts,
  updateFetchedProducts,
  setProductsLoading,
  setProductsError,
  existingFetchedProducts = []
) => {
  // Early return if no selected products
  if (!selectedProducts || selectedProducts.length === 0) {
    updateFetchedProducts([])
    setProductsError('')
    return
  }

  // Extract product IDs sorted by priority
  const productIds = extractProductIdsByPriority(selectedProducts, existingFetchedProducts)
  
  if (productIds.length === 0) {
    updateFetchedProducts([])
    setProductsError('')
    return
  }

  setProductsLoading(true)
  setProductsError('')

  try {
    // Create search array and query params
    const searchArr = createSearchArray(productIds)
    const queryParams = createQueryParams()

    // Fetch products from API
    const response = await getProductList(queryParams, searchArr)
    const data = response?.data ?? {}
    const items = data.items ?? data

    if (Array.isArray(items)) {
      // Reorder products to match the order of product IDs sent in request
      const reorderedProducts = reorderProductsByRequestOrder(items, productIds)
      
      // Enhance products with priority and details
      const enhancedProducts = enhanceProductsWithDetails(reorderedProducts)
      
      // Preserve existing details for products that already have them
      const productsWithPreservedDetails = preserveProductDetails(existingFetchedProducts, enhancedProducts)
      
      updateFetchedProducts(productsWithPreservedDetails)
    } else {
      updateFetchedProducts([])
      setProductsError('Invalid response format')
    }
  } catch (error) {
    console.error('Error fetching products by IDs:', error)
    setProductsError('Failed to fetch product details')
    updateFetchedProducts([])
  } finally {
    setProductsLoading(false)
  }
}
